import { Calendar, ShieldCheck, Wrench } from 'lucide-react';
import { MeterBar, type MeterTone } from '../ui/MeterBar';
import type { Vehicle } from '../../types/vehicle';
import {
  vehicleMaintenance,
  type MaintenanceItem,
  type MaintenanceKey,
  type MaintenanceStatus,
} from '../../utils/maintenance';

const TONE: Record<MaintenanceStatus, MeterTone> = {
  expired: 'danger',
  due_soon: 'warn',
  valid: 'ok',
  unknown: 'muted',
};

const ICON: Record<MaintenanceKey, React.ReactNode> = {
  soat: <Calendar size={11} />,
  tecnomecanica: <Calendar size={11} />,
  insurance: <ShieldCheck size={11} />,
  oil: <Wrench size={11} />,
};

function ItemBar({ item }: { item: MaintenanceItem }) {
  return (
    <MeterBar
      label={item.label}
      caption={item.caption}
      ratio={item.ratio}
      tone={TONE[item.status]}
      icon={ICON[item.key]}
      hint={item.hint}
    />
  );
}

interface VehicleMaintenanceBarsProps {
  vehicle: Pick<Vehicle, 'documents' | 'oilChange'>;
  /**
   * `card`: solo el ítem más grave + cuántos más hay (la tarjeta de la lista no
   * tiene espacio para cuatro barras). `detail`: los cuatro, con sus explicaciones.
   */
  mode: 'card' | 'detail';
}

/**
 * Envoltorio de dominio de `MeterBar`: pinta los vencimientos de un vehículo.
 * Toda la lógica (umbral, estados, qué lleva barra) vive en `utils/maintenance.ts`;
 * aquí solo se traduce estado → tono e ícono.
 */
export function VehicleMaintenanceBars({ vehicle, mode }: VehicleMaintenanceBarsProps) {
  const maintenance = vehicleMaintenance(vehicle);

  if (mode === 'detail') {
    return (
      <div className="maintenance-bars">
        {maintenance.items.map(item => <ItemBar key={item.key} item={item} />)}
      </div>
    );
  }

  // Un vehículo sin ninguna fecha NO dice "vigente": dice que no sabe.
  if (maintenance.withData === 0) {
    return (
      <div className="maintenance-bars">
        <MeterBar label="Vencimientos" caption="Sin datos" tone="muted" icon={<Calendar size={11} />} />
      </div>
    );
  }

  // Los otros ítems SIN datos no se cuentan en el "+N más": "Sin datos" no es información.
  const others = maintenance.withData - 1;
  return (
    <div className="maintenance-bars">
      <ItemBar item={maintenance.mostUrgent} />
      {others > 0 && <span className="maintenance-bars__more">+{others} más</span>}
    </div>
  );
}
